import { FieldValue, increment } from 'firebase/firestore';
import uniqid from 'uniqid';
import { Product, Store, StoreLog, Supply, Wallet, WalletLog, Worker } from '../../apiTypes';
import TransactionCategories from '../../constants/transactionCategories';
import { createBatch } from '../../utils/api';
import { Map } from '../../utils/map';

type Props = {
  cafeSlug: string;
  supply: DbDocument<Supply>;
  store: DbDocument<Store>;
  worker: DbDocument<Worker>;
  shouldAddTransaction: boolean;
  productsMap: Map<DbDocument<Product>>;
  wallet?: DbDocument<Wallet>;
  totalCost: number;
  time: number;
};

export const acceptSupply = async ({
  supply,
  cafeSlug,
  store,
  worker,
  productsMap,
  wallet,
  shouldAddTransaction,
  totalCost,
  time,
}: Props) => {
  if (store && supply && worker) {
    const batch = createBatch();

    const diff: Record<string, number> = {};
    const updates: Record<`residues.${string}`, FieldValue> = {};

    supply.components.forEach(({ id, qnt }) => {
      if (qnt) {
        diff[id] = qnt;
        updates[`residues.${id}`] = increment(qnt);
      }
    });

    batch.update<Supply>(`cafes/${cafeSlug}/supply/${supply.id}`, {
      status: 'closed',
      time,
      storeId: store.id,
      walletId: wallet?.id || null,
      totalCost,
    });

    batch.update<Store>(`cafes/${cafeSlug}/stores/${store.id}`, updates);

    batch.set<StoreLog>(`cafes/${cafeSlug}/storeLogs/${uniqid()}`, {
      time: new Date().getTime(),
      workerId: worker.id,
      workerName: worker.name,
      storeId: store.id,
      type: 'supply',
      diff,
      info: {
        type: 'supply',
        number: supply.number,
        id: supply.id,
      },
    });

    if (shouldAddTransaction && wallet && totalCost) {
      const walletLog: WalletLog = {
        time: new Date().getTime(),
        sum: -totalCost,
        workerId: worker.id,
        workerName: worker.name,
        wallet: wallet.id,
        bankCommission: 0,
        category: TransactionCategories.supply,
        total: wallet.balance - totalCost,
        info: { type: 'supply', id: supply.id, number: supply.number },
        comment: '',
        type: 'supply',
      };

      batch.update<Wallet>(`cafes/${cafeSlug}/wallets/${wallet.id}`, { balance: increment(-totalCost) });
      batch.set<WalletLog>(`cafes/${cafeSlug}/wallets/${wallet.id}/logs/${uniqid()}`, walletLog);
    }

    supply.components.forEach((component) => {
      const product = productsMap[component.id];
      if (product && (component.price !== product.price || product.price !== product.prevPrice)) {
        batch.update<Product>(`cafes/${cafeSlug}/products/${product.id}`, {
          prevPrice: product.price || component.price || 0,
          price: component.price || 0,
        });
      }
    });

    await batch.commit();
  }
};
